'use client'

import { useState } from 'react'

import { FeatureTooltipProvider, type TooltipData } from '@/components/client/product/feature-tooltip'
import { useTranslation } from '@/hooks/client/use-translation'

import { PriceFeatureBaseline } from './price-feature-baseline'
import type { PriceLevelTab } from './price-section-types'
import { PriceUpgradeDiffRow } from './price-upgrade-diff-row'
import type { UpgradeComparison } from './upgrade-comparison'

interface PriceUpgradePanelProps {
  selectedLevel: PriceLevelTab
  levels: PriceLevelTab[]
  comparisons: UpgradeComparison[]
  tooltips: Record<string, TooltipData>
}

export const PriceUpgradePanel = ({ selectedLevel, levels, comparisons, tooltips }: PriceUpgradePanelProps) => {
  const { t } = useTranslation()
  const [targetId, setTargetId] = useState(comparisons[0]?.levelId)
  const [expanded, setExpanded] = useState(false)
  const comparison = comparisons.find((entry) => entry.levelId === targetId) ?? comparisons[0]
  if (!comparison) {
    return null
  }
  const highlights = comparison.highlights
  const visible = expanded ? highlights : highlights.slice(0, 6)
  const baseline = levels.find((level) => level.id === comparison.levelId)
  const title =
    baseline && baseline.sortOrder > selectedLevel.sortOrder
      ? t('product.panel.title', 'What {{tier}} adds over {{lower}}', {
          tier: selectedLevel.name,
          lower: baseline.name,
        })
      : t('product.purchase.comparisonTitle', '{{tier}} compared with {{baseline}}', {
          tier: `${selectedLevel.name} ${selectedLevel.levelName}`,
          baseline: baseline ? `${baseline.name} ${baseline.levelName}` : comparison.levelName,
        })
  return (
    <FeatureTooltipProvider tooltips={tooltips}>
      <div className="mt-2 rounded-xl border border-navy-20 bg-white px-4 py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-2">
          <h3 className="text-navy m-0 text-sm font-semibold">{title}</h3>
          <PriceFeatureBaseline
            levels={levels}
            comparisons={comparisons}
            selectedId={comparison.levelId}
            onSelect={(levelId) => {
              setTargetId(levelId)
              setExpanded(false)
            }}
          />
        </div>
        {highlights.length === 0 ? (
          <p className="mt-4 text-sm text-navy-60">
            {t('product.panel.noAdditional', 'No additional features are listed for this comparison.')}
          </p>
        ) : (
          <ul aria-live="polite" className="mt-3 grid grid-cols-1 gap-x-6 sm:grid-cols-2">
            {visible.map((highlight) => (
              <PriceUpgradeDiffRow key={highlight.name} highlight={highlight} />
            ))}
          </ul>
        )}
        {highlights.length > 6 && (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            data-tracking="ignore"
            className="mt-3 min-h-11 cursor-pointer text-sm font-semibold text-lapis hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lapis"
          >
            {expanded
              ? t('product.showLess', 'Show Less')
              : t('product.purchase.allDifferences', 'Show all {{count}} differences', {
                  count: String(highlights.length),
                })}
          </button>
        )}
      </div>
    </FeatureTooltipProvider>
  )
}
